import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink } from '../components/Nav';
import { Badge, Banner, Button, CopyButton, Loading, Spinner } from '../components/ui';
import { ApiError, errorMessage } from '../lib/api';
import { shareUrl } from '../lib/env';
import { dateTime, timecode } from '../lib/format';
import { Comments } from '../player/Comments';
import { Player } from '../player/Player';
import { useViewBeacon } from '../player/useViewBeacon';
import { qk } from '../queries/keys';
import { fetchShare } from '../queries/share';

/**
 * /v/:shareId — the page everybody who is not a Slate user sees.
 *
 * It has to work with no account, on a phone, on the first tap. It also has to
 * work *before the video is ready*: the share link is handed out the instant
 * recording starts, so arriving early is normal, not an error.
 */

function ProcessingPanel({ title }: { title: string }) {
  return (
    <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
      <div className="clapper-stripes absolute inset-x-0 top-0 h-2 opacity-[0.12]" aria-hidden="true" />
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 px-6 text-center">
        <Spinner size={22} className="text-chalk-faint" />
        <p className="text-h4 text-chalk">{title}</p>
        <p className="max-w-sm text-body text-chalk-dim">
          This one is still being processed. It usually takes a minute or two — leave this page
          open and it will start on its own.
        </p>
      </div>
    </div>
  );
}

export function SharePage({ shareId }: { shareId: string }) {
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);

  const query = useQuery({
    queryKey: qk.share(shareId),
    queryFn: () => fetchShare(shareId),
    // Poll while the pipeline works, then stop. `false` turns polling off.
    refetchInterval: (result) => {
      const status = result.state.data?.status;
      return status === 'processing' || status === 'uploading' ? 5000 : false;
    },
    retry: (failureCount, error) => !(error instanceof ApiError && error.isNotFound) && failureCount < 2,
  });

  const video = query.data;
  const playable = video?.playback ?? null;
  const beacon = useViewBeacon(shareId, Boolean(video));

  useEffect(() => {
    if (video?.title) document.title = `${video.title} · Slate`;
    return () => {
      document.title = 'Slate';
    };
  }, [video?.title]);

  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="border-b border-hairline">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between gap-3 px-4 sm:px-6">
          <AppLink to={user ? '/' : '/login'} aria-label="Slate">
            <Wordmark />
          </AppLink>
          {user ? (
            <AppLink to="/">
              <Button size="sm">Your library</Button>
            </AppLink>
          ) : (
            <AppLink to="/signup" className="text-caption text-chalk-faint hover:text-chalk-dim">
              Record your own
            </AppLink>
          )}
        </div>
        <ClapperRule />
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
        {query.isLoading ? <Loading label="Loading this recording" /> : null}

        {query.isError ? (
          <div className="mx-auto max-w-md py-16 text-center">
            <h1 className="text-h3 text-chalk">
              {query.error instanceof ApiError && query.error.isNotFound
                ? 'This link does not go anywhere'
                : 'That did not load'}
            </h1>
            <p className="mt-2 text-body text-chalk-dim">
              {query.error instanceof ApiError && query.error.isNotFound
                ? 'The recording may have been deleted, or the link may be private and meant for somebody signed in.'
                : errorMessage(query.error)}
            </p>
            {!user && query.error instanceof ApiError && query.error.isNotFound ? (
              <AppLink to="/login" className="mt-5 inline-block">
                <Button>Sign in and try again</Button>
              </AppLink>
            ) : null}
          </div>
        ) : null}

        {video ? (
          <div className="space-y-6">
            {video.status === 'failed' ? (
              <Banner tone="danger" title="This recording failed to process">
                Something went wrong converting it. If it is yours, the original upload is still
                safe — get in touch and it can be reprocessed.
              </Banner>
            ) : null}

            {playable ? (
              <Player
                playback={playable}
                title={video.title}
                videoRef={videoRef}
                onProgress={beacon.report}
                onEnded={beacon.flush}
              />
            ) : (
              <ProcessingPanel title={video.title} />
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-h3 text-chalk">{video.title}</h1>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-chalk-faint">
                  {video.author?.name ? <span>{video.author.name}</span> : null}
                  <span>{dateTime(video.createdAt)}</span>
                  {video.durationSec ? (
                    <span data-timecode className="font-mono">
                      {timecode(video.durationSec)}
                    </span>
                  ) : null}
                  {video.status !== 'ready' ? (
                    <Badge tone="caution" pulse>
                      Processing
                    </Badge>
                  ) : null}
                </p>
              </div>
              <CopyButton value={shareUrl(shareId)} size="sm" />
            </div>

            <div className="border-t border-hairline pt-6">
              <Comments shareId={shareId} videoRef={videoRef} playerReady={Boolean(playable)} />
            </div>
          </div>
        ) : null}
      </main>

      <footer className="border-t border-hairline">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-5 text-caption text-chalk-faint sm:px-6">
          <span>Recorded with Slate</span>
          <AppLink to="/signup" className="hover:text-chalk-dim">
            Make your own →
          </AppLink>
        </div>
      </footer>
    </div>
  );
}
